/**
 * Performance monitoring utilities
 */

/**
 * Performance metrics class
 */
class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
  }

  /**
   * Start timing a performance measurement
   *
   * @param {string} label - The measurement label
   * @returns {Function} Function to end the measurement
   */
  startMeasure(label) {
    const startTime = performance.now();
    const startMark = `${label}-start`;

    if (performance.mark) {
      performance.mark(startMark);
    }

    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime;

      // Store metric
      this.recordMetric(label, duration);

      // Create performance measure if supported
      if (performance.measure) {
        try {
          performance.measure(label, startMark);
        } catch (e) {
          // Mark may not exist, ignore
        }
      }

      return duration;
    };
  }

  /**
   * Record a performance metric
   *
   * @param {string} label - The metric label
   * @param {number} duration - The duration in milliseconds
   */
  recordMetric(label, duration) {
    if (!this.metrics.has(label)) {
      this.metrics.set(label, {
        count: 0,
        totalDuration: 0,
        minDuration: Infinity,
        maxDuration: -Infinity,
        avgDuration: 0
      });
    }

    const metric = this.metrics.get(label);
    metric.count++;
    metric.totalDuration += duration;
    metric.minDuration = Math.min(metric.minDuration, duration);
    metric.maxDuration = Math.max(metric.maxDuration, duration);
    metric.avgDuration = metric.totalDuration / metric.count;

    this.metrics.set(label, metric);
  }

  /**
   * Get metrics for a specific label
   *
   * @param {string} label - The metric label
   * @returns {Object|null} The metric data or null
   */
  getMetric(label) {
    return this.metrics.get(label) || null;
  }

  /**
   * Get all metrics
   *
   * @returns {Object} All metrics
   */
  getAllMetrics() {
    const result = {};
    this.metrics.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  /**
   * Clear all metrics
   */
  clearMetrics() {
    this.metrics.clear();

    // Clear performance marks and measures
    if (performance.clearMarks) {
      performance.clearMarks();
    }
    if (performance.clearMeasures) {
      performance.clearMeasures();
    }
  }

  /**
   * Log metrics to console
   *
   * @param {string} label - Optional label to log specific metric
   */
  logMetrics(label = null) {
    if (label) {
      const metric = this.getMetric(label);
      if (metric) {
        console.log(`[Performance] ${label}:`, metric);
      }
    } else {
      console.log('[Performance] All Metrics:', this.getAllMetrics());
    }
  }

  /**
   * Measure a function execution
   *
   * @param {string} label - The measurement label
   * @param {Function} fn - The function to measure
   * @returns {Promise|any} The function result
   */
  async measure(label, fn) {
    const endMeasure = this.startMeasure(label);

    try {
      const result = await fn();
      endMeasure();
      return result;
    } catch (error) {
      endMeasure();
      throw error;
    }
  }
}

// Export singleton instance
export default new PerformanceMonitor();

// Export class for custom instances
export { PerformanceMonitor };

/**
 * Simple timing wrapper
 *
 * @param {string} label - The timer label
 * @param {Function} fn - The function to time
 * @returns {Promise|any} The function result
 */
export async function timeExecution(label, fn) {
  const start = performance.now();
  try {
    const result = await fn();
    const duration = performance.now() - start;
    console.debug(`[Timer] ${label}: ${duration.toFixed(2)}ms`);
    return result;
  } catch (error) {
    const duration = performance.now() - start;
    console.error(`[Timer] ${label} failed after ${duration.toFixed(2)}ms:`, error);
    throw error;
  }
}
